import React, { useState } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { StreamingSession } from '../state/Sessions';
import SessionStatus from './SessionStatus';

export interface SessionStatusFilterProps {
  value: StreamingSession['status'] | '';
  onChange: (status: StreamingSession['status'] | '') => void;
}

const statuses = ['ACTIVE', 'CONNECTING', 'IDLE', 'STOPPED'] as const;
const statusDescription = {
  ACTIVE: 'A user is currently connected and using the session.',
  CONNECTING: 'A user is connecting to the session.',
  IDLE: 'A user has disconnected temporarily.',
  STOPPED: 'The session has been terminated.',
} as const;

export default function SessionStatusFilter({
  value,
  onChange,
}: SessionStatusFilterProps) {
  const handleChange = (event: any) => {
    onChange(event.target.value as StreamingSession['status'] | '');
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange('');
  };

  return (
    <FormControl sx={{ minWidth: 280 }} size="small">
      <InputLabel id="session-status-filter-label">Filter by status</InputLabel>
      <Select
        labelId="session-status-filter-label"
        id="session-status-filter"
        value={value}
        label="Filter by status"
        onChange={handleChange}
        endAdornment={
          value ? (
            <IconButton
              aria-label="Remove status filter"
              size="small"
              onClick={handleClear}
              sx={{ mr: 1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null
        }
        renderValue={(selected) =>
          selected ? <SessionStatus status={selected} /> : ''
        }
      >
        {statuses.map((status) => (
          <MenuItem key={status} value={status} sx={{ fontSize: '1.53rem' }}>
            <Box sx={{ py: 0.5 }}>
              <SessionStatus status={status} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '1.4rem',
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {statusDescription[status]}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
